import type { AssetFilters } from '~/types/filters'
import type { TableReportType } from '~/types/report'
import type {
  ReportCellValue,
  ReportColumnDescriptor,
  ReportColumnValueType,
  ReportPreviewBody,
  ReportPreviewPage,
  ReportPreviewRequest,
  ReportSort,
  ReportWindow
} from '~/types/report-preview'
import type {
  ServiceColumnDescriptor,
  ServiceDefinitionColumn,
  ServiceDraftPreviewRequest,
  ServiceFilters,
  ServiceGrainKey,
  ServiceParams,
  ServicePresetKey,
  ServicePreviewPage,
  ServicePreviewRequest,
  ServiceRunFormat,
  ServiceRunRequest,
  ServiceScope,
  ServiceScopeDimension,
  ServiceSort,
  ServiceWindow
} from '~/types/report-service'
import { canonical } from '~/utils/reports/preview-request'
import { getReportPreviewPreset, type ReportWindowMode } from '~/utils/reports/preview-presets'
import { ReportScopeError } from '~/utils/reports/service-errors'
import { getReportScopePreset } from '~/utils/reports/scope-presets'
import { getServiceScopeDimensions, toServicePresetKey, toServiceWindowPreset } from '~/utils/reports/service-presets'
import { extractScopeIds } from '~/utils/reports/scope-conditions'

export type AssetIdResolver = (filter: AssetFilters) => Promise<number[]>

export interface ServiceRequestDeps {
  resolveAssetIds: AssetIdResolver
  timezone?: string
}

export interface ServicePresetRequest {
  presetKey: ServicePresetKey
  params: ServiceParams
  sort: ServiceSort[]
}

export interface ServiceTargetShape {
  windowMode: ReportWindowMode
  scopeDimensions: ServiceScopeDimension[]
  singleAsset?: boolean
}

export interface ServiceTargetRequestDeps extends ServiceRequestDeps {
  filters?: ServiceFilters
}

const VALUE_TYPES: ReportColumnValueType[] = ['string', 'number', 'boolean', 'datetime', 'enum', 'duration', 'percent']

const toNumbers = (values: (string | number)[] | undefined): number[] | undefined => {
  if (!values || values.length === 0) return undefined
  const ids = values.map(Number).filter((id) => Number.isInteger(id))
  return ids.length > 0 ? ids : undefined
}

const compact = <T extends object>(input: T): T =>
  Object.fromEntries(Object.entries(input).filter(([, value]) => value !== undefined)) as T

export const resolveTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export const toServiceWindow = (window: ReportWindow, mode: ReportWindowMode): ServiceWindow => {
  if ('preset' in window) return { preset: toServiceWindowPreset(window.preset) }
  if ('from' in window && mode !== 'instant') return { from: window.from, to: window.to }
  return { to: window.to }
}

export const toServiceFilters = (reportType: TableReportType, body: ReportPreviewBody): ServiceFilters | undefined => {
  const filters: ServiceFilters = {}
  switch (reportType) {
    case 'alert': {
      const ruleIds = toNumbers(body.alertRulesIds)
      if (ruleIds) filters.alertRuleIds = ruleIds
      if (body.alertStatus) filters.alertStatus = body.alertStatus
      break
    }
    case 'assets-registry': {
      if (body.eventTypes?.length) filters.eventTypes = body.eventTypes
      if (body.reportFilter?.alertKeys?.length) filters.alertKeys = body.reportFilter.alertKeys
      if (body.reportFilter?.communicationStatus) filters.communicationStatus = body.reportFilter.communicationStatus
      break
    }
    case 'user-log': {
      const users = toNumbers(body.users)
      if (users) filters.users = users
      if (body.actions?.length) filters.actions = body.actions
      if (body.eventTypes?.length) filters.eventTypes = body.eventTypes
      break
    }
    default:
      break
  }
  return Object.keys(filters).length > 0 ? filters : undefined
}

const needsAssetResolution = (filter: AssetFilters): boolean => {
  if (filter.assetIds?.length) return true
  if (filter.search?.trim()) return true
  return !extractScopeIds(filter.conditions).supported
}

const scopeFromAssetFilter = (filter: AssetFilters): ServiceScope => {
  const ids = extractScopeIds(filter.conditions)
  return compact({
    assetTypeIds: filter.assetTypeIds.length > 0 ? filter.assetTypeIds : undefined,
    groupIds: ids.groupIds.length > 0 ? ids.groupIds : undefined,
    zoneIds: ids.zoneIds.length > 0 ? ids.zoneIds : undefined,
    routeIds: ids.routeIds.length > 0 ? ids.routeIds : undefined
  })
}

const isEmptyScope = (scope: ServiceScope): boolean =>
  !scope.licenceWide &&
  !scope.assetTypeIds?.length &&
  !scope.groupIds?.length &&
  !scope.zoneIds?.length &&
  !scope.routeIds?.length &&
  !scope.assetIds?.length &&
  !scope.viewId &&
  !scope.filter

const keepSupportedDimensions = (scope: ServiceScope, dimensions: ServiceScopeDimension[]): ServiceScope =>
  compact<ServiceScope>({
    assetTypeIds: dimensions.includes('assetTypeIds') ? scope.assetTypeIds : undefined,
    groupIds: dimensions.includes('groupIds') ? scope.groupIds : undefined,
    zoneIds: dimensions.includes('zoneIds') ? scope.zoneIds : undefined,
    routeIds: dimensions.includes('routeIds') ? scope.routeIds : undefined,
    assetIds: dimensions.includes('assetIds') ? scope.assetIds : undefined,
    viewId: scope.viewId,
    filter: scope.filter,
    licenceWide: scope.licenceWide ? true : undefined
  })

const withoutChosenDimension = (singleAsset: boolean): ServiceScope => {
  if (singleAsset) throw new ReportScopeError('singleAsset', 0)
  return { licenceWide: true }
}

export const toServiceScopeFor = async (
  body: ReportPreviewBody,
  dimensions: ServiceScopeDimension[],
  resolveAssetIds: AssetIdResolver,
  singleAsset = false,
  selection?: ServiceScope
): Promise<ServiceScope> => {
  if (dimensions.length === 0) return {}

  if (selection) {
    const scope = keepSupportedDimensions(selection, dimensions)
    if (singleAsset && scope.assetIds?.length !== 1)
      throw new ReportScopeError('singleAsset', scope.assetIds?.length ?? 0)
    return isEmptyScope(scope) ? withoutChosenDimension(singleAsset) : scope
  }

  if (body.reportFilter) {
    const scope = keepSupportedDimensions(
      {
        assetTypeIds: body.reportFilter.assetTypeIds.length > 0 ? body.reportFilter.assetTypeIds : undefined,
        groupIds: body.reportFilter.groupIds.length > 0 ? body.reportFilter.groupIds : undefined,
        zoneIds: body.reportFilter.zoneIds.length > 0 ? body.reportFilter.zoneIds : undefined,
        routeIds: body.reportFilter.routeIds.length > 0 ? body.reportFilter.routeIds : undefined
      },
      dimensions
    )
    return isEmptyScope(scope) ? withoutChosenDimension(singleAsset) : scope
  }

  const filter = body.filter
  if (!filter) return withoutChosenDimension(singleAsset)

  const explicit = filter.assetIds?.length ? filter.assetIds : undefined
  if (singleAsset || needsAssetResolution(filter)) {
    const assetIds = explicit && !filter.search?.trim() ? explicit : await resolveAssetIds(filter)
    if (singleAsset && assetIds.length !== 1) throw new ReportScopeError('singleAsset', assetIds.length)
    if (assetIds.length === 0) throw new ReportScopeError('empty')
    return keepSupportedDimensions({ assetIds }, dimensions)
  }

  const scope = keepSupportedDimensions(scopeFromAssetFilter(filter), dimensions)
  return isEmptyScope(scope) ? withoutChosenDimension(singleAsset) : scope
}

export const toServiceScope = async (
  reportType: TableReportType,
  body: ReportPreviewBody,
  resolveAssetIds: AssetIdResolver,
  selection?: ServiceScope
): Promise<ServiceScope> =>
  toServiceScopeFor(
    body,
    getServiceScopeDimensions(reportType),
    resolveAssetIds,
    getReportScopePreset(reportType).singleAsset,
    selection
  )

export const toServiceSort = (sort: ReportSort[]): ServiceSort[] => {
  const first = sort[0]
  return first ? [{ key: first.key, dir: first.dir }] : []
}

export const toServicePresetRequest = async (
  request: ReportPreviewRequest,
  deps: ServiceRequestDeps
): Promise<ServicePresetRequest> => {
  const preset = getReportPreviewPreset(request.reportType)
  const { window, locale, scope: selection, body } = request.params
  const scope = await toServiceScope(request.reportType, body, deps.resolveAssetIds, selection)
  const filters = toServiceFilters(request.reportType, body)
  return {
    presetKey: toServicePresetKey(request.reportType),
    params: compact({
      window: toServiceWindow(window, preset.windowMode),
      scope,
      filters,
      locale,
      timezone: deps.timezone ?? resolveTimezone()
    }),
    sort: toServiceSort(request.sort)
  }
}

export const toServicePreviewRequest = async (
  request: ReportPreviewRequest,
  deps: ServiceRequestDeps
): Promise<{ presetKey: ServicePresetKey; body: ServicePreviewRequest }> => {
  const preset = await toServicePresetRequest(request, deps)
  return {
    presetKey: preset.presetKey,
    body: {
      params: preset.params,
      sort: preset.sort,
      cursor: request.cursor,
      pageSize: request.pageSize
    }
  }
}

export const toServiceRunRequest = async (
  request: ReportPreviewRequest,
  formats: ServiceRunFormat[],
  deps: ServiceRequestDeps
): Promise<ServiceRunRequest> => {
  const preset = await toServicePresetRequest(request, deps)
  return { presetKey: preset.presetKey, params: preset.params, sort: preset.sort, formats }
}

export const toServiceTargetParams = async (
  request: ReportPreviewRequest,
  shape: ServiceTargetShape,
  deps: ServiceTargetRequestDeps
): Promise<ServiceParams> => {
  const { window, locale, scope: selection, body } = request.params
  const scope = await toServiceScopeFor(
    body,
    shape.scopeDimensions,
    deps.resolveAssetIds,
    shape.singleAsset ?? false,
    selection
  )
  const filters = deps.filters && Object.keys(deps.filters).length > 0 ? deps.filters : undefined
  return compact({
    window: toServiceWindow(window, shape.windowMode),
    scope,
    filters,
    locale,
    timezone: deps.timezone ?? resolveTimezone()
  })
}

export const toServiceDefinitionPreviewRequest = async (
  request: ReportPreviewRequest,
  shape: ServiceTargetShape,
  deps: ServiceTargetRequestDeps,
  columns?: string[]
): Promise<ServicePreviewRequest> => ({
  params: await toServiceTargetParams(request, shape, deps),
  sort: toServiceSort(request.sort),
  ...(columns && columns.length > 0 ? { columns: [...columns] } : {}),
  cursor: request.cursor,
  pageSize: request.pageSize
})

export const toServiceDraftPreviewRequest = async (
  request: ReportPreviewRequest,
  grain: ServiceGrainKey,
  columns: ServiceDefinitionColumn[],
  shape: ServiceTargetShape,
  deps: ServiceTargetRequestDeps
): Promise<ServiceDraftPreviewRequest> => ({
  grain,
  columns,
  params: await toServiceTargetParams(request, shape, deps),
  sort: toServiceSort(request.sort),
  cursor: request.cursor,
  pageSize: request.pageSize
})

export const toServiceDefinitionRunRequest = async (
  request: ReportPreviewRequest,
  definitionId: string,
  formats: ServiceRunFormat[],
  shape: ServiceTargetShape,
  deps: ServiceTargetRequestDeps
): Promise<ServiceRunRequest> => ({
  definitionId,
  params: await toServiceTargetParams(request, shape, deps),
  sort: toServiceSort(request.sort),
  formats
})

const normalizeCell = (value: unknown): ReportCellValue => {
  if (value === undefined || value === null) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value
  return JSON.stringify(value)
}

export const toReportColumnDescriptor = (column: ServiceColumnDescriptor): ReportColumnDescriptor => ({
  key: column.key,
  labelKey: column.labelKey,
  label: column.label,
  valueType: VALUE_TYPES.includes(column.valueType as ReportColumnValueType)
    ? (column.valueType as ReportColumnValueType)
    : 'string',
  ...(column.unit ? { unit: column.unit } : {}),
  sortable: column.sortable,
  align: column.align === 'right' ? 'right' : 'left'
})

export const toServicePreviewPage = (
  page: ServicePreviewPage,
  hidden: TableReportType | string[]
): ReportPreviewPage => {
  const hiddenKeys = Array.isArray(hidden) ? hidden : getReportPreviewPreset(hidden).hiddenKeys
  const columns = page.columns ?? []
  const visible = columns
    .map((column, index) => ({ column, index }))
    .filter(({ column }) => !hiddenKeys.includes(column.key))
  return {
    columns: visible.map(({ column }) => toReportColumnDescriptor(column)),
    rows: (page.rows ?? []).map((row) => visible.map(({ index }) => normalizeCell(row[index]))),
    nextCursor: page.nextCursor ?? null,
    meta: {
      truncated: page.meta.truncated,
      ...(page.meta.queryMs !== undefined ? { queryMs: page.meta.queryMs } : {}),
      ...(page.meta.freshAsOf ? { freshAsOf: page.meta.freshAsOf } : {}),
      rowCountHint: page.meta.rowCountHint
    }
  }
}

const canonicalFilter = (filter: AssetFilters): string => {
  const { page: _page, pageSize: _pageSize, ...rest } = filter
  return JSON.stringify(canonical(rest))
}

export const createMemoisedAssetIdResolver = (fetchIds: AssetIdResolver): AssetIdResolver => {
  const cache = new Map<string, Promise<number[]>>()
  return (filter: AssetFilters): Promise<number[]> => {
    const key = canonicalFilter(filter)
    const cached = cache.get(key)
    if (cached) return cached
    const pending = fetchIds(filter).catch((error: unknown) => {
      cache.delete(key)
      throw error
    })
    cache.set(key, pending)
    return pending
  }
}
